import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { MatterBannerKind, MatterRead } from './schemas'
import type { MatterMemberRead } from '@/matter/components/manage-members/schemas'

export type MattersRecord = Partial<{ [matter_id: string]: MatterRead }>

interface MatterMembersEntry {
  members: MatterMemberRead[]
  loading: boolean
  error: boolean
  /* Set once the first fetch succeeds; a later failed or in-flight poll never resets it, so the loaded list stays visible. */
  initialLoadComplete: boolean
  /* Monotonic id of the latest request; a response tagged with an older id is dropped so a slow fetch can't overwrite fresher data. */
  requestId: number
  /* Timestamp of the last successful refresh, used by the listener's cache guard. */
  lastRefresh: number | null
}

export enum MatterDialogType {
  Create = 'create',
  Edit = 'edit',
  Delete = 'delete',
  AcceptInvite = 'acceptInvite',
  ManageMembers = 'manage-members',
}

interface MatterDialogState {
  dialogType: MatterDialogType | null
  matterId: string | null
  /** When true, after a successful create, set the new matter as the active matter. */
  switchToMatterOnCreate: boolean
  /** When true, after a successful create, navigate to the matter overview (`/dashboard/m/:id`). */
  navigateToMatterOnCreate: boolean
  /** Which surface opened the dialog. Plain string (not an analytics enum) to keep this store decoupled
   * from analytics — see README.md ("Matter jurisdiction nudges") in src/analytics for why. */
  source: string | null
}

const initialDialogState: MatterDialogState = {
  dialogType: null,
  matterId: null,
  switchToMatterOnCreate: false,
  navigateToMatterOnCreate: false,
  source: null,
}

export type MattersState = {
  matters: MattersRecord
  /** Owner-only Outlook workspace, never a shared matter destination. */
  personalWorkspace?: MatterRead | null
  membersByMatterId: Partial<{ [matter_id: string]: MatterMembersEntry }>
  /* Setup banners the signed-in user has dismissed, per matter. Kept out of `matters` because that
   * record is also written by the polled matter list, which doesn't carry per-user dismissals. */
  dismissedBannersByMatterId: Partial<{ [matter_id: string]: MatterBannerKind[] }>
  initialLoadComplete: boolean
  lastRefresh: number | null
  loading: boolean
  error: boolean
  /* Monotonic id of the latest matter-list fetch; a response tagged with an older id is dropped so a slow poll can't overwrite a fresher forced refresh. */
  requestId: number
  dialog: MatterDialogState
  activeMatterId: string | null
}

const initialState: MattersState = {
  matters: {},
  personalWorkspace: null,
  membersByMatterId: {},
  dismissedBannersByMatterId: {},
  initialLoadComplete: false,
  lastRefresh: null,
  loading: false,
  error: false,
  requestId: 0,
  dialog: initialDialogState,
  activeMatterId: null,
}

/**
 * Matters Slice
 *
 * Manages state related to matters.
 *
 * A Matter represents a user-owned workspace that organizes conversations,
 * files, and client information for a specific case or project.
 */
export const mattersSlice = createSlice({
  name: 'mattersState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshMatterList: (_state, _action: PayloadAction<{ forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },
    refreshMatterDetail: (_state, _action: PayloadAction<{ matterId: string; forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,
    setPersonalWorkspace: (state, action: PayloadAction<MatterRead | null>) => {
      state.personalWorkspace = action.payload
    },

    // ============== Matter Actions ============== >
    // Claim a new latest matter-list fetch: bump the request id and enter the loading state.
    // The listener drops any response whose id is no longer the latest.
    beginMatterListRefresh: (state, action: PayloadAction<number>) => {
      state.requestId = action.payload
      state.loading = true
      state.error = false
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Set error state
    setError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload
    },

    // Set initial load complete
    setInitialLoadComplete: (state, action: PayloadAction<boolean>) => {
      state.initialLoadComplete = action.payload
    },

    // Set last refresh
    setLastRefresh: (state, action: PayloadAction<number | null>) => {
      state.lastRefresh = action.payload
    },

    // Replace the full list of matters while preserving existing unchanged entries
    setMatters: (state, action: PayloadAction<MatterRead[]>) => {
      // Build a new matters record
      const newMatters: Record<string, MatterRead> = {}

      action.payload.forEach((matter) => {
        const existingMatter = state.matters[matter.id]

        // Reuse the existing object (preserves referential equality, avoids re-renders) only when
        // nothing the table renders has changed. Ownership transfers and membership changes don't
        // bump updated_at, so also compare owner, name, and membership fields.
        const unchanged =
          existingMatter &&
          existingMatter.user_id === matter.user_id &&
          existingMatter.name === matter.name &&
          existingMatter.updated_at === matter.updated_at &&
          existingMatter.member_count === matter.member_count &&
          existingMatter.viewer_role === matter.viewer_role &&
          existingMatter.invited_at === matter.invited_at &&
          existingMatter.accepted_at === matter.accepted_at

        newMatters[matter.id] = unchanged ? existingMatter : matter
      })

      // Perform a single replacement of all matters
      state.matters = newMatters
    },

    // Add or update a single matter
    upsertMatter(state, action: PayloadAction<MatterRead>) {
      const matterId = action.payload.id
      state.matters[matterId] = action.payload
    },

    // Remove a matter by id
    removeMatter(state, action: PayloadAction<{ matterId: string }>) {
      const { matterId } = action.payload
      delete state.matters[matterId]
    },

    // Set the currently active matter ID (or clear it with null)
    setActiveMatterId: (state, action: PayloadAction<{ matterId: string | null; source?: string | null }>) => {
      const { matterId } = action.payload
      const currentActiveMatterId = state.activeMatterId

      if (currentActiveMatterId !== matterId) {
        state.activeMatterId = matterId
      }
    },

    // ============== Matter Members Actions ============== >
    /* Claim a new latest members refresh for one matter: bump its request id and enter the loading state. */
    matterMembersRefreshStarted: (state, action: PayloadAction<{ matterId: string; requestId: number }>) => {
      const { matterId, requestId } = action.payload
      const existing = state.membersByMatterId[matterId]
      state.membersByMatterId[matterId] = {
        members: existing?.members ?? [],
        loading: true,
        error: false,
        initialLoadComplete: existing?.initialLoadComplete ?? false,
        requestId,
        lastRefresh: existing?.lastRefresh ?? null,
      }
    },

    matterMembersRefreshSucceeded: (
      state,
      action: PayloadAction<{ matterId: string; members: MatterMemberRead[]; requestId: number; lastRefresh: number }>
    ) => {
      const { matterId, members, requestId, lastRefresh } = action.payload
      const existing = state.membersByMatterId[matterId]
      // Only the refresh this matter is currently waiting on may write. Drop the response if the
      // entry was cleared (e.g. on sign-out) or another refresh has since claimed it, so a slow
      // response can't recreate cleared state or overwrite fresher members.
      if (!existing || requestId !== existing.requestId) return
      state.membersByMatterId[matterId] = {
        members,
        loading: false,
        error: false,
        initialLoadComplete: true,
        requestId,
        lastRefresh,
      }
    },

    matterMembersRefreshFailed: (state, action: PayloadAction<{ matterId: string; requestId: number }>) => {
      const { matterId, requestId } = action.payload
      const existing = state.membersByMatterId[matterId]
      if (!existing || requestId !== existing.requestId) return
      existing.loading = false
      existing.error = true
      // The detail response is what carries the dismissal set, so a failure means it isn't coming.
      // Settle it as "none dismissed" rather than leaving setup banners hidden behind a request
      // that won't succeed. Only when still unknown, so a dismissal already made here survives.
      state.dismissedBannersByMatterId[matterId] ??= []
    },

    removeMatterMembers: (state, action: PayloadAction<{ matterId: string }>) => {
      delete state.membersByMatterId[action.payload.matterId]
    },

    // ============== Dismissed Banner Actions ============== >
    /* Merge a server-supplied set of dismissals into what's already known for a matter.
     * A union rather than a replacement: dismissal is permanent and only ever grows, so a detail
     * poll that started before a dismissal was saved can't briefly un-dismiss a banner. */
    matterDismissedBannersReceived: (state, action: PayloadAction<{ matterId: string; kinds: MatterBannerKind[] }>) => {
      const { matterId, kinds } = action.payload
      const existing = state.dismissedBannersByMatterId[matterId] ?? []
      state.dismissedBannersByMatterId[matterId] = [...new Set([...existing, ...kinds])]
    },

    /* Hide a banner immediately on click, before the save round-trips. */
    matterBannerDismissed: (state, action: PayloadAction<{ matterId: string; kind: MatterBannerKind }>) => {
      const { matterId, kind } = action.payload
      const existing = state.dismissedBannersByMatterId[matterId] ?? []
      if (existing.includes(kind)) return
      state.dismissedBannersByMatterId[matterId] = [...existing, kind]
    },

    /* Drop an optimistic dismissal that failed to save, so the banner comes back. */
    matterBannerDismissalReverted: (state, action: PayloadAction<{ matterId: string; kind: MatterBannerKind }>) => {
      const { matterId, kind } = action.payload
      const existing = state.dismissedBannersByMatterId[matterId]
      if (!existing) return
      state.dismissedBannersByMatterId[matterId] = existing.filter((existingKind) => existingKind !== kind)
    },

    // ============== Matter Dialog Actions ============== >
    // Open the create matter dialog
    openDialog: (
      state,
      action: PayloadAction<{
        dialogType: MatterDialogType
        matterId?: string
        switchToMatterOnCreate?: boolean
        navigateToMatterOnCreate?: boolean
        source?: string
      }>
    ) => {
      state.dialog = {
        dialogType: action.payload.dialogType,
        matterId: action.payload.matterId ?? null,
        switchToMatterOnCreate: action.payload.switchToMatterOnCreate ?? false,
        navigateToMatterOnCreate: action.payload.navigateToMatterOnCreate ?? false,
        source: action.payload.source ?? null,
      }
    },

    // Close the dialog
    closeDialog: (state) => {
      state.dialog = initialDialogState
    },
  },
})

// Actions
export const MattersActions = mattersSlice.actions

export default mattersSlice.reducer
